import { Page } from '@app/components/page/Page';
import { UserIcon } from '@app/components/users/UserIcon';
import { useToast } from '@app/core/notifications';
import { updateUser } from '@app/modules/admin/api';
import { typedKeys, useRouter } from '@mi-front/core';
import { ViewLoader } from '@mi-front/ui';
import { Box } from '@mui/material';
import pick from 'lodash/pick';
import { FormattedMessage, useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';
import { useErrorHandler } from '../../../../../core/hooks/useErrorHandler';
import { UsersBreadcrumbs } from '../components/UsersBreadcrumbs';
import { useApiUser } from '../hooks/useApiUser';
import { EditUserValues, editUserSchema } from './edit-user.schema';
import { EditUserForm } from './EditUserForm';

export function EditUserPage() {
  const { id } = useParams<{ id: string }>();
  const { formatMessage } = useIntl();
  const { history } = useRouter();
  const { onApiError } = useErrorHandler();
  const { notifySuccess } = useToast();

  const userQuery = useApiUser(id);

  if (!userQuery.data) {
    return <ViewLoader />;
  }

  const onSubmit = async (data: EditUserValues) => {
    try {
      await updateUser(id!, data);
      notifySuccess(
        formatMessage({
          id: 'admin.users.notifications.updated',
          defaultMessage: 'Usuario actualizado',
        }),
      );
      history.push(`/admin/users/${id}`);
    } catch (error) {
      onApiError(error);
    }
  };

  const title = formatMessage({
    id: 'admin.users.editScreen.title',
    defaultMessage: 'Editar usuario',
  });
  const user = userQuery.data!;
  const userDisplayName = user.name ? `${user.name} ${user.surname || ''}` : user.email;

  return (
    <Page data-cy="users-edit-screen" htmlTitle={title}>
      <Page.Header
        icon={<UserIcon />}
        title={
          <>
            {userDisplayName}
            <Box component="span" sx={{ opacity: 0.7 }}>
              {' '}
              — {title}
            </Box>
          </>
        }
        breadcrumbs={
          <UsersBreadcrumbs
            title={<FormattedMessage id="common.actions.edit" defaultMessage="Editar" />}
            active={user}
          />
        }
      />
      <Page.Content>
        <EditUserForm
          values={{
            ...pick(user, typedKeys(editUserSchema.shape)),
            groupId: user.groupId ?? 'NONE',
          }}
          onSubmit={onSubmit}
        />
      </Page.Content>
    </Page>
  );
}
